"use client";

import { useCallback, useState } from "react";

import { QuizCTA } from "~/components/features/quiz";
import Carousel from "~/componentsDS/Carousel";
import Reveal from "~/componentsDS/Reveal";
import TextWithRegisteredMark from "~/componentsDS/TextWithRegisteredMark";
import Heading from "~/componentsDS/Typography/Heading";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import { CarouselApi, CarouselContentVisible, CarouselItem } from "~/components/ui/carousel";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { QUIZ_ROUTES } from "~/constants/quizRoutes";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import PcosBubbleCard from "../components/PcosBubbleSlide";
import { PcosBubbleSliderData } from "../data";

const CTA_TEXT = "See If Oestra® Is Right for You";

const BubbleCarouselSection = () => {
    const sendEvent = useTrackEvent();
    const [activeSlideIndex, setActiveSlideIndex] = useState(0);

    const onSelect = useCallback(
        (emblaApi: CarouselApi) => {
            if (!emblaApi) return;

            const idx = emblaApi.selectedScrollSnap();
            const item = PcosBubbleSliderData[idx];

            if (!item) return;

            setActiveSlideIndex(idx);

            sendEvent({
                event: "user_interaction",
                value: {
                    event_name: GTM_EVENT_NAMES.LP_INTERACTION,
                    widget: "pcos_and_weight",
                    meta_data: {
                        image_title: item.analyticsTitle,
                        image_count: idx + 1,
                    },
                },
            });
        },
        [sendEvent],
    );

    const activeSlide = PcosBubbleSliderData[activeSlideIndex];

    return (
        <section className="w-full bg-white px-5 py-16 md:px-20 md:py-24">
            <div className="mx-auto max-w-[1500px]">
                <Reveal className="mb-8 w-fit max-md:mx-auto md:mx-auto md:mb-4">
                    <Heading
                        as="h2"
                        size="sm"
                        sizes={{ md: "md" }}
                        className="mb-4 text-center text-[32px] text-primary500 max-md:mx-auto max-md:max-w-[320px] max-md:leading-[1.1] md:text-[48px]"
                    >
                        PMOS/PCOS is
                        <span className="font-featureDisplay text-[28px] font-normal italic md:text-[43px]">
                            {" "}not
                        </span>{" "}
                        caused by weight gain
                    </Heading>

                    <Paragraph
                        size="lg"
                        className="pb-[22px] text-center font-normal leading-[1.3] text-primary500 max-md:max-w-[340px] md:pb-0"
                    >
                        You’ve likely been told that losing weight will fix your PMOS/PCOS.
                    </Paragraph>

                    <Paragraph size="lg" className="text-center font-medium leading-[1.3] text-primary500">
                        But what if that’s backwards?
                    </Paragraph>
                </Reveal>

                <div className="mb-8 w-[100vw] overflow-hidden max-md:-mx-5 max-md:px-6 md:w-full">
                    <Carousel
                        loop={false}
                        variant="compact"
                        compactPaginationAlign="right"
                        compactPaginationBg="bg-trueBlack/10 backdrop-blur-[25px]"
                        compactPaginationDotColor="bg-gray100"
                        opts={{
                            align: "center",
                            skipSnaps: false,
                            dragFree: false,
                        }}
                        className="w-full"
                        onSelect={onSelect}
                    >
                        <CarouselContentVisible className="max-md:ml-[-10px] md:ml-[-10px]">
                            {PcosBubbleSliderData.map((step, index) => (
                                <CarouselItem
                                    key={step.analyticsTitle}
                                    className="max-w-[338px] flex-shrink-0 basis-full pl-2 md:max-w-[500px] md:basis-1/2 md:pl-[10px] xl:basis-1/3"
                                >
                                    <PcosBubbleCard stepNumber={index + 1} {...step} />
                                </CarouselItem>
                            ))}
                        </CarouselContentVisible>
                    </Carousel>
                </div>

                <Reveal className="mb-6 w-fit md:mx-auto md:mb-8">
                    <p className="text-center text-[16px] font-semibold leading-[1.3] text-primary500 md:text-[18px]">
                        You can’t fix PMOS/PCOS by losing weight if your hormones are the reason you can’t lose weight.
                    </p>
                </Reveal>

                <div className="flex justify-center">
                    <QuizCTA
                        variant="primary"
                        href={QUIZ_ROUTES.HORMONE_IMBALANCE}
                        size="md"
                        eventData={{
                            event_name: GTM_EVENT_NAMES.LP_INTERACTION,
                            widget: "pcos_and_weight",
                            meta_data: {
                                click_text: CTA_TEXT,
                                image_title: activeSlide?.analyticsTitle,
                                image_count: activeSlideIndex + 1,
                            },
                        }}
                    >
                        <span className="inline-block">
                            <TextWithRegisteredMark text={CTA_TEXT} />
                        </span>
                    </QuizCTA>
                </div>
            </div>
        </section>
    );
};

export default BubbleCarouselSection;